import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';

import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEqual, omit, throttle } from 'lodash';
import { finalize, switchMap, tap } from 'rxjs/operators';

import { I18nService } from '@app/core';
import {
  User,
  AlertButtonType,
  SignerInput,
  SelectOption,
  CustomSignerInput,
  SignerMainAttributes,
  DocumentInput,
  SecurityVerificationEnum,
  DynamicTemplate,
  DynamicTemplateInput,
  DynamicTemplateVariable,
  DynamicTemplateVariableInput,
  DynamicTemplateSigner,
  DocumentMainTab,
  DynamicTemplateData,
  DynamicDocumentFieldsTypes,
  SignerInputContext,
  DynamicTemplateThemeEnum,
  LanguageEnum,
  GraphQLResponse,
  InlineFieldContext
} from '@app/models';
import { AppService, DocumentService, ErrorHandlerService, NotyService, DynamicDocumentService, UserService, WhitelabelService, OSOption, ValidationService } from '@app/services';
import { AlertModalService, LoaderService, SelectComponent } from '@app/shared';

import { WordsToNumberPipe } from '@app/shared/pipes/words-to-number.pipe';

import { DocumentFormService } from '@app/pages/documents/shared/document-form.service';
import { idColors } from '@app/pages/documents/shared/floating-fields-id-colors';

import { Editor } from '@tiptap/core';
import { EditorView } from '@tiptap/pm/view';
import { Node } from '@tiptap/pm/model';
import { StarterKit } from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Table from '@tiptap/extension-table';
import FileHandler from '@tiptap/extension-file-handler';
import { Clipboard, FindAndReplace, NumberedHeading, Subtitle, InlineField, Indent, CustomHistory, CustomImage, Summary, AutoCapitalize, Level } from './editor-extensions';
import { PaginationPlus } from 'tiptap-pagination-plus';
import { PaginationTable } from 'tiptap-table-plus';

import { CreateEditFieldsFormComponent, ModalPublicProperties } from './create-edit-fields-form/create-edit-fields-form.component';
import { FindAndReplaceModalService } from './find-and-replace-modal/find-and-replace-modal.service';
import { SourceCodeModalService } from './source-code-modal/source-code-modal.service';

const { TablePlus, TableRowPlus, TableCellPlus, TableHeaderPlus } = PaginationTable;

interface SavedDynamicTemplateData {
  template: DynamicTemplateInput;
  variables: DynamicTemplateVariable[];
  signers: CustomSignerInput[];
}
interface Range {
  from: number;
  to: number;
}
interface Coordinates {
  top: string;
  left: string;
}

enum ToolbarDropdownOptions {
  Edit = 'edit',
  Insert = 'insert',
  View = 'view',
  Format = 'format'
}

enum ToolbarDropdownSecondaryOptions {
  Image = 'image',
  Table = 'table',
  Alignment = 'alignment',
  TextColor = 'textColor',
  BackgroundColor = 'backgroundColor',
  None = 'none'
}

@Component({
  selector: 'app-documents-dynamic-templates',
  templateUrl: './documents-dynamic-templates.component.html',
  styleUrl: './documents-dynamic-templates.component.scss',
  standalone: false,
  providers: [NgbModalConfig, NgbModal]
})
export class DocumentsDynamicTemplatesComponent implements OnInit, OnDestroy {
  document = { name: '', expiration: {}, configs: {}, locale: {} } as DocumentInput;

  currentUser: User;

  isUnsaved = false;
  isRendered = false;
  isLoading = false;

  modalQuery: DynamicTemplateVariableInput;
  variableInputs: DynamicTemplateVariableInput[];
  dynamicTemplateInput: DynamicTemplateInput = {
    id: '',
    name: '',
    description: '',
    keywords: [''],
    category: 'contracts',
    is_private: false,
    content: '',
    lang: LanguageEnum.PtBr,
    theme: DynamicTemplateThemeEnum.Modern
  };
  savedDynamicTemplateData: SavedDynamicTemplateData;
  readonly DynamicTemplateThemeEnum = DynamicTemplateThemeEnum;

  validationErrors: Record<string, string> = {};

  currentOS: OSOption = this.dynamicDocumentService.detectOS();

  tableMenuCoordinates: Coordinates = { top: '0px', left: '0px' };

  showCreateEditSidebar = false;
  currentRightSidebarOption: 'signers' | 'options' = 'signers';

  readonly ToolbarDropdownOptions = ToolbarDropdownOptions;
  readonly ToolbarDropdownSecondaryOptions = ToolbarDropdownSecondaryOptions;
  toolbarOpenedOption: ToolbarDropdownOptions = null;

  editor!: Editor;

  currentView: 'desktop' | 'mobile' = 'desktop';
  file?: File;

  isShowNumberedTitles = false;

  form!: FormGroup;
  // categories = this.dynamicDocumentService.getDynamicDocumentCategoryOptions(this.translateService);
  languages: SelectOption[];
  themes = this.dynamicDocumentService.getDynamicDocumentThemeOptions(this.translateService);

  blockOptions: SelectOption[] = this.dynamicDocumentService.getDynamicDocumentBlockOptions(this.translateService);
  defaultAlignments: Record<string, string> = this.dynamicDocumentService.getDynamicDocumentDefaultAligments(this.dynamicTemplateInput.theme);
  toolbarColors: string[][] = this.dynamicDocumentService.getDynamicDocumentToolbarColors();
  tableMenuIcons: Record<string, SafeHtml> = this.dynamicDocumentService.getTableMenuIcons();

  currentBlock = 'body';

  // Variáveis para campos

  customFields?: DynamicTemplateVariable[];
  fieldToEditData?: ModalPublicProperties['variable'];

  showNewFieldBubble = false;
  newFieldBubbleCoordinates: Coordinates = { top: '0px', left: '0px' };

  // Variáveis para signatários

  signers: CustomSignerInput[] = [];

  readonly idColors = idColors;
  readonly SignerInputContext = SignerInputContext;

  @ViewChild('editor', { static: false }) editorRef!: ElementRef<HTMLElement>;
  @ViewChild('blockSelector', { static: false }) blockSelector?: SelectComponent;
  @ViewChild('createEditFieldsComponent') createEditFieldsComponent: CreateEditFieldsFormComponent;

  constructor(
    public appService: AppService,
    public whitelabelService: WhitelabelService,
    public translateService: TranslateService,
    public documentService: DocumentService,
    public findAndReplaceModalService: FindAndReplaceModalService,
    public sourceCodeModalService: SourceCodeModalService,
    public i18nService: I18nService,
    public wordsToNumberPipe: WordsToNumberPipe,
    private notyService: NotyService,
    private errorHandlerService: ErrorHandlerService,
    private dynamicDocumentService: DynamicDocumentService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private alertModalService: AlertModalService,
    private formBuilder: UntypedFormBuilder,
    private documentFormService: DocumentFormService,
    private loaderService: LoaderService,
    private validationService: ValidationService
  ) {
    this.form = this.formBuilder.group({
      dynamicTemplateOptions: this.formBuilder.group({
        name: '',
        description: '',
        keywords: '',
        category: 'contracts',
        is_private: false,
        lang: '',
        theme: DynamicTemplateThemeEnum.Modern
      })
    });

    this.languages = this.i18nService.supportedLanguagesAsSelectOptions;
  }

  ngOnInit() {
    if (!this.appService.isAutentiqueDomain()) {
      this.notyService.error(this.translateService.instant('error_without_permission_page'));
      this.navigateToNewDocument();
    }

    if (window.innerWidth <= 992) {
      this.navigateToNewDocument();
    }

    this.dynamicTemplateInput.id = this.activatedRoute.snapshot.paramMap.get('id');
    this.userService.getCurrentUser({ fetchPolicy: 'cache-first' }).subscribe({
      next: user => {
        if (!(user.email || '').match(/@autentique.com.br$/) && user.organization.id !== 10126135) {
          this.navigateToNewDocument();
        }

        this.currentUser = user;
        this.form.get('dynamicTemplateOptions.lang').setValue(user?.locale?.language || this.i18nService.language);
        this.dynamicTemplateInput.lang = this.appService.getLanguageEnumFromString(user?.locale?.language || this.i18nService.language);

        // if (!user.subscription.has_premium_features) {
        //   this.form.get('dynamicDocumentOptions.isPrivate').disable();
        // }

        this.form.get('dynamicTemplateOptions.lang').valueChanges.subscribe({
          next: language => this.setTemplateLanguage(language)
        });
        this.form.get('dynamicTemplateOptions.theme').valueChanges.subscribe({
          next: theme => this.changeTemplateTheme(theme)
        });

        if (this.dynamicTemplateInput.id !== null) {
          this.isLoading = true;
          this.dynamicDocumentService
            .template({ id: this.dynamicTemplateInput.id })
            .pipe(
              tap((dynamicTemplate: DynamicTemplate) => {
                this.dynamicTemplateInput = {
                  id: dynamicTemplate.id,
                  name: dynamicTemplate.name,
                  content: '',
                  description: dynamicTemplate.description,
                  keywords: dynamicTemplate.keywords,
                  category: dynamicTemplate.category,
                  is_private: false,
                  lang: this.appService.getLanguageEnumFromString(dynamicTemplate.lang || this.currentUser.locale.language),
                  theme: dynamicTemplate.theme || DynamicTemplateThemeEnum.Modern
                };

                const currentLang = this.languages.find(lang => lang.value.replace(/-|_/g, '').toLowerCase() == dynamicTemplate.lang.replace(/-|_/g, '').toLowerCase()).key;

                this.form.get('dynamicTemplateOptions').setValue({
                  name: dynamicTemplate.name,
                  description: dynamicTemplate.description,
                  keywords: dynamicTemplate.keywords?.join(', '),
                  category: dynamicTemplate.category,
                  is_private: false,
                  lang: currentLang,
                  theme: dynamicTemplate.theme || DynamicTemplateThemeEnum.Modern
                });
              }),
              switchMap((dynamicTemplate: DynamicTemplate) => this.dynamicDocumentService.getDynamicDocumentData(dynamicTemplate.path)),
              tap((dynamicTemplateData: DynamicTemplateData) => this.setDynamicTemplateData(dynamicTemplateData)),
              finalize(() => {
                this.isLoading = false;
              })
            )
            .subscribe({
              error: (error: GraphQLResponse) => {
                this.errorHandlerService.handle(error);
                this.router.navigate(['/'], { replaceUrl: true });
              }
            });
        } else {
          this.isRendered = true;

          this.addNewSigner();
          this.signers[0].action = null;
          this.addNewSigner();

          this.customFields = this.dynamicDocumentService.getDynamicDocumentDefaultFields(this.translateService);

          this.applyDefaultAligments();

          setTimeout(() => {
            this.savedDynamicTemplateData = { template: cloneDeep(this.dynamicTemplateInput), variables: cloneDeep(this.customFields), signers: cloneDeep(this.signers) };
            this.editor?.commands.focus();
          });
        }
      }
    });

    // INICIALIZAÇÃO DO EDITOR
    setTimeout(() => this.initializeEditor());

    setTimeout(() => {
      this.onSignerChange(this.signers[this.signers.length - 1] as CustomSignerInput);
      this.isUnsaved = false;
    }, 1000);
  }

  ngOnDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  @HostListener('window:mousedown', ['$event'])
  onWindowMousedown(event: MouseEvent) {
    setTimeout(() => {
      if (this.toolbarOpenedOption) {
        if (
          !['toolbar__option', 'toolbar__dropdown', 'dropdown__option', 'fal', 'table__dropdown', 'switch-field'].some(classToCheck =>
            (event.target as HTMLElement).classList.contains(classToCheck)
          ) &&
          !['option', 'table__grid', 'table__full', 'dropdown__option'].some(classToCheck => (event.target as HTMLElement).parentElement.classList.contains(classToCheck))
        ) {
          setTimeout(() => {
            document.querySelectorAll(`.toolbar__dropdown.editor-dropdown`).forEach((dropdown: HTMLElement) => (dropdown.style.display = 'none'));
            this.toolbarOpenedOption = null;
          });
        }
      }
    });
  }

  setDynamicTemplateData(documentFile: DynamicTemplateData) {
    const content = this.dynamicDocumentService.removeOnlyNumberFieldsFromHTML(documentFile.template.content);
    this.dynamicTemplateInput.content = content;

    const regexToFindNumberedTitles = /<h[2-4][^>]*data-show-numbering="true"/g;
    this.isShowNumberedTitles = regexToFindNumberedTitles.test(content);

    this.customFields = [];
    documentFile.variables
      .filter((variable: DynamicTemplateVariable) => variable.data_signer_id === null && !/^\d+$/.test(variable.name))
      .forEach((field: DynamicTemplateVariable) => {
        this.customFields.push({ ...field });
      });

    this.savedDynamicTemplateData = { template: cloneDeep(this.dynamicTemplateInput), variables: cloneDeep(this.customFields), signers: [] };
    this.savedDynamicTemplateData.template.content = content;

    if (documentFile.signers[0].variable_id !== 0) {
      this.addNewSigner();
      this.signers[0].action = null;
    }
    this.signers = this.signers.concat(this.dynamicDocumentService.retrieveSignersData(documentFile.signers as DynamicTemplateSigner[]));

    setTimeout(() => {
      this.savedDynamicTemplateData.signers = cloneDeep(this.signers);

      if (this.editor) {
        this.editor
          .chain()
          .setContent(this.dynamicTemplateInput.content || '')
          .setMeta('addToHistory', false)
          .run();

        setTimeout(() => {
          this.editor.chain().setTextSelection(1).focus().setMeta('addToHistory', false).run();
          this.editorRef.nativeElement.scrollTop = 0;
          this.setCurrentBlockSelectValue();
          this.isRendered = true;
        });
      }

      this.applyDefaultAligments();
    });
  }

  initializeEditor() {
    this.editor = new Editor({
      element: this.editorRef.nativeElement,
      extensions: [
        Clipboard.configure({
          onCustomEvent: ((range: Range) => {
            this.convertTypedOrPastedFields(true, range);
            this.applyDefaultAligments();
            this.removeNonExistentFields();
          }).bind(this)
        }),
        NumberedHeading.configure({
          levels: [1, 2, 3, 4],
          setNewParagraphAlignment: this.setNewParagraphAlignment.bind(this),
          getShowNumberedHeadings: () => this.isShowNumberedTitles
        }),
        Subtitle.configure({
          setNewParagraphAlignment: this.setNewParagraphAlignment.bind(this)
        }),
        Underline,
        Superscript,
        Subscript,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'subtitle']
        }),
        TextStyle,
        Color,
        Indent,
        Highlight.configure({
          multicolor: true
        }),
        InlineField.configure({
          context: InlineFieldContext.Editor,
          handleInlineFieldClick: this.handleClickOnInlineField.bind(this),
          convertTextIntoFields: this.convertTypedOrPastedFields.bind(this)
        }),
        FindAndReplace.configure({
          openModal: this.openFindAndReplaceModal.bind(this)
        }),
        TableRowPlus,
        TableCellPlus,
        TableHeaderPlus,
        TablePlus.configure({
          resizeHandleStyle: {
            background: 'transparent',
            borderRadius: '0',
            position: 'absolute',
            height: '100%',
            width: '4px',
            transform: 'translateX(-50%)',
            top: '0'
          }
        }),
        Table.configure({
          resizable: true
        }),
        CustomImage.configure({
          allowBase64: true,
          notifyImageInsertionError: (() => this.notyService.error(this.translateService.instant('notyService.unableToInsertImage'))).bind(this)
        }),
        FileHandler.configure({
          allowedMimeTypes: ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'],
          onDrop: (currentEditor, files, pos) => this.handleImageInsertion(currentEditor, files, pos, null, true),
          onPaste: (currentEditor, files, htmlContent) => this.handleImageInsertion(currentEditor, files, null, htmlContent)
        }),
        PaginationPlus.configure({
          pageHeight: 1123,
          pageGap: 16,
          pageGapBorderSize: 1,
          pageBreakBackground: 'var(--clr-structure-bg-50)',
          pageHeaderHeight: 94,
          pageFooterHeight: 94,
          footerRight: '{page}',
          footerLeft: '',
          headerRight: '',
          headerLeft: '',
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          contentMarginTop: 0,
          contentMarginBottom: 0
        }),
        StarterKit.configure({
          heading: false,
          horizontalRule: false,
          hardBreak: false,
          code: false,
          codeBlock: false
        }),
        CustomHistory.configure({
          onCustomEvent: this.removeNonExistentFields.bind(this)
        }),
        Summary.configure({
          getSummaryContent: (() => this.dynamicDocumentService.getDynamicDocumentSummaryHTML(this.editor)).bind(this)
        }),
        AutoCapitalize
      ],
      onUpdate: ({ editor, transaction }) => {
        this.setCurrentBlockSelectValue();

        if (!transaction.getMeta('paste') && !transaction.getMeta('isConvertion')) {
          if (document.querySelector('app-find-and-replace-modal')) {
            setTimeout(() => this.convertTypedOrPastedFields());
          } else {
            this.convertTypedOrPastedFields();
          }
        }

        this.positionFloatingElements();
      },
      onSelectionUpdate: () => {
        this.setCurrentBlockSelectValue();
        setTimeout(() => this.positionFloatingElements());
      },
      onPaste: () => {
        setTimeout(() => {
          this.applyDefaultAligments();
          this.removeNonExistentFields();
        });
      }
    });

    this.createEditFieldsComponent.editor = this.editor;

    if (!this.dynamicTemplateInput.id) {
      this.setBlockType('body');
    }
  }

  // FUNÇÕES PARA GERENCIAR SIGNATÁRIOS

  onSignerChange(signer: CustomSignerInput) {
    this.validationErrors = {};

    const signerFieldsLength = this.signers.length;
    const usedSignerFieldsLength = this.signers.length - 1;
    const currentSignerIndex = this.signers.findIndex(thisSigner => thisSigner === signer);

    if (signerFieldsLength === usedSignerFieldsLength) {
      this.addNewSigner();
    } else if (signerFieldsLength > usedSignerFieldsLength + 1) {
      this.signers
        .map((thisSigner, index) => (!thisSigner[thisSigner._extra.signerMainAttribute] ? index : -1))
        .filter(index => index >= 0 && index !== currentSignerIndex)
        .forEach(index => this.signers.splice(index, 1));
    }

    if (signer?.action === null) {
      signer._extra.showOptions = false;
      signer.security_verifications = [];
      signer._extra.showSecurityVerifications = false;
      signer._extra.showCpfVerification = false;
      signer._extra.showSmsVerification = false;
      signer._extra.fallbackEnabled = true;
    }

    if (signer && !signer._extra.isEditable) {
      signer[signer._extra.signerMainAttribute] = '';
      signer._extra.signerMainAttribute = SignerMainAttributes.Email;

      if (signer.whatsapp === '') {
        delete signer.whatsapp;
      }
    }
  }

  addNewSigner() {
    const mainAttribute = SignerMainAttributes.Email;
    this.signers.push({
      [mainAttribute]: '',
      action: this.documentService.roles[0],
      configs: {},
      _extra: { signerMainAttribute: mainAttribute, phoneNumber: '', documentType: SecurityVerificationEnum.Manual, fallbackEnabled: true, isEditable: false },
      security_verifications: []
    });
  }

  removeSigner(signerIndex: number) {
    this.signers.splice(signerIndex, 1);

    document.querySelectorAll('.field--inline').forEach((field: HTMLElement) => {
      let filledByNow = parseInt(field.getAttribute('data-signer-id'));
      if (filledByNow >= signerIndex) {
        const signer = filledByNow == signerIndex ? '0' : (filledByNow - 1).toString();
        const color = filledByNow == signerIndex ? '0' : (parseInt(field.getAttribute('data-color-id')) - 1).toString();

        this.dynamicDocumentService.updateInlineFieldAttrs(this.editor, field, { signer, color });
      }
    });
  }

  verifyValidSigners(): boolean {
    return this.signers.filter(signer => signer.action !== null).length > 0;
  }

  verifyDuplicateSigners(): boolean {
    let verifiedSigners: CustomSignerInput[] = new Array();

    this.signers.forEach(signer => {
      if (
        verifiedSigners.findIndex(
          verifiedSigner =>
            verifiedSigner?.action == signer?.action &&
            verifiedSigner[verifiedSigner?._extra?.signerMainAttribute] == signer[signer?._extra?.signerMainAttribute] &&
            !!signer[signer?._extra?.signerMainAttribute]
        ) === -1
      ) {
        verifiedSigners.push(signer);
      }
    });

    return this.signers.length !== verifiedSigners.length;
  }

  // FUNÇÕES PARA GERENCIAR A TOOLBAR

  openToolbarDropdown(option: ToolbarDropdownOptions) {
    const dropdown = document.querySelector(`[data-dropdown-option=${option}]`) as HTMLElement;

    this.openOptionDropdown();
    this.toolbarOpenedOption = option;

    document.querySelectorAll('.toolbar__dropdown').forEach(d => {
      (d as HTMLElement).style.display = 'none';
    });
    dropdown.style.display = 'flex';
  }

  openOptionDropdown(option?: ToolbarDropdownSecondaryOptions) {
    document.querySelectorAll('.option__dropdown').forEach(d => {
      (d as HTMLElement).style.display = 'none';
    });

    if (option) {
      (document.querySelector(`[data-dropdown-option=${option}]`) as HTMLElement).style.display = 'flex';
    }
  }

  // FUNÇÕES PARA O EDITOR

  callClipboardAction(action: 'copy' | 'cut' | 'paste' | 'pastePlain') {
    return this.editor.chain().focus()[action]().run();
  }

  callHistoryAction(action: 'undo' | 'redo') {
    return this.editor.chain().focus()[action]().run();
  }

  selectAll() {
    this.editor.chain().focus().selectAll().run();
  }

  clearFormats() {
    this.editor.chain().focus().clearNodes().unsetAllMarks().run();
  }

  isActive(format: string, options: Record<string, string | number> = {}) {
    return this.editor && this.editor.isActive(format, options);
  }

  // Formatação

  toggleBold() {
    this.editor.chain().focus().toggleBold().run();
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run();
  }

  toggleUnderline() {
    this.editor.chain().focus().toggleUnderline().run();
  }

  toggleStrike() {
    this.editor.chain().focus().toggleStrike().run();
  }

  toggleScript(script: 'super' | 'sub') {
    if (script === 'super') {
      this.editor.chain().focus().toggleSuperscript().run();
    } else {
      this.editor.chain().focus().toggleSubscript().run();
    }
  }

  toggleList(type: 'ul' | 'ol') {
    if (type === 'ol') {
      this.editor.chain().focus().toggleOrderedList().run();
    } else {
      this.editor.chain().focus().toggleBulletList().run();
    }
  }

  indent(value: 1 | -1) {
    this.editor.commands.focus();

    if (value === 1) {
      this.editor.commands.indent();
    } else {
      this.editor.commands.outdent();
    }
  }

  // Cores

  setBackground(color?: string) {
    if (!color) {
      color = (document.querySelector('#background__picker') as HTMLInputElement).value;
    }

    this.editor.chain().focus().setHighlight({ color }).run();
  }

  unsetBackground() {
    this.editor.chain().focus().unsetHighlight().run();
  }

  setColor(color?: string) {
    if (!color) {
      color = (document.querySelector('#color__picker') as HTMLInputElement).value;
    }

    this.editor.chain().focus().setColor(color).run();
  }

  unsetColor() {
    this.editor.chain().focus().unsetColor().run();
  }

  // Aplicação de blocos

  setBlockType(type: string) {
    if (this.getCurrentBlockType() !== type) {
      if (/^h[1-4]$/.test(type)) {
        this.editor
          .chain()
          .focus()
          .setHeading({ level: parseInt(type[1]) as Level })
          .updateHeadingNumbers()
          .run();
        if (type[1] === '1') {
          this.setAlignment('center');
        }
      } else if (type === 'subtitle') {
        this.editor.chain().focus().setNode('subtitle').run();
      } else if (type === 'body') {
        this.editor.chain().focus().setParagraph().run();
      } else if (type === 'blockquote') {
        if (this.isActive('heading') || this.isActive('subtitle')) {
          this.setBlockType('body');
        }
        this.editor.chain().focus().setBlockquote().run();
      }
    }

    let currentBlockType = this.editor.isActive('listItem') ? 'li' : this.getCurrentBlockType();
    if (currentBlockType === 'body') {
      currentBlockType = 'p';
    }
    this.setAlignment(this.defaultAlignments[currentBlockType]);
  }

  getCurrentBlockType() {
    if (this.isActive('heading')) {
      for (const level of [1, 2, 3, 4]) {
        if (this.isActive('heading', { level: level })) {
          return `h${level}`;
        }
      }
    }
    if (this.isActive('subtitle')) {
      return 'subtitle';
    }
    if (this.isActive('blockquote')) {
      return 'blockquote';
    }
    return 'body';
  }

  setCurrentBlockSelectValue() {
    const currentFormat = this.blockOptions.find(option => option.key == this.getCurrentBlockType());

    this.blockSelector.value = currentFormat?.value;
    this.blockSelector.selectedOption = currentFormat;
    this.blockSelector.inputValue = '';
    document.querySelector('#blockSelector label')?.setAttribute('data-selected-format', currentFormat?.value);

    this.currentBlock = currentFormat.key;
  }

  showNumberedTitles() {
    this.isShowNumberedTitles = !this.isShowNumberedTitles;
    this.dynamicDocumentService.updateEditorNodesAttributes(this.editor, 'heading', { 'data-show-numbering': this.isShowNumberedTitles });
  }

  // Alinhamento

  setAlignment(align: string) {
    this.editor.chain().focus().setTextAlign(align).run();
  }

  applyDefaultAligments(forceApply: boolean = false) {
    const prevSelection = this.editor?.state.selection || 0;
    this.editor?.state.doc.descendants((node, pos, parent) => {
      if (!forceApply && (node.attrs.textAlign || parent.attrs.textAlign)) return;

      this.editor?.chain().setTextSelection(pos).setMeta('addToHistory', false).run();

      let currentBlockType = this.editor.isActive('listItem') ? 'li' : this.getCurrentBlockType();
      if (currentBlockType === 'body') {
        currentBlockType = 'p';
      }

      this.setAlignment(this.defaultAlignments[currentBlockType]);
    });
    this.editor?.chain().setTextSelection(prevSelection).setMeta('addToHistory', false).run();
  }

  setNewParagraphAlignment() {
    setTimeout(() => this.setAlignment('justify'));
  }

  // Código-fonte

  openSourceCode() {
    this.sourceCodeModalService.open({ srcCode: this.editor.getHTML() }).subscribe({ next: (newSrcCode: string) => this.updateSourceCode(newSrcCode) });
  }

  updateSourceCode(newSrcCode?: string) {
    newSrcCode = this.dynamicDocumentService.removeOnlyNumberFieldsFromHTML(newSrcCode);

    if (newSrcCode != null) {
      this.editor.chain().setContent(newSrcCode).focus().run();
      this.applyDefaultAligments(true);
      this.removeNonExistentFields();
    }
  }

  // Localizar e substituir

  openFindAndReplaceModal() {
    if (document.querySelector('app-source-code-modal')) return;

    const properties = { editor: this.editor, textToSearch: '' };

    const selection = this.editor.state.selection;
    if (!selection.empty) {
      properties.textToSearch = this.editor.state.doc.textBetween(selection.from, selection.to);
    }

    this.findAndReplaceModalService.open(properties).subscribe();
  }

  // Visualizações

  toggleDynamicTemplateView(view: 'desktop' | 'mobile') {
    if (this.currentView === view) {
      return;
    }

    this.positionFloatingElements();

    this.currentView = view;
    this.editor.commands.focus();
  }

  positionFloatingElements() {
    this.positionNewFieldBubble(this.editor);
    this.positionTableMenu(this.editor);
  }

  // Sumário

  insertSummary() {
    this.editor.chain().focus().insertSummary().run();
  }

  // Tabelas

  onTableUnitHover(x: number, y: number, isDropdownOption: boolean) {
    const gridContext: string = isDropdownOption ? '.toolbar__dropdown .dropdown__option ' : '.toolbar__formats ';

    let tableUnits = document.querySelectorAll(gridContext + '.table__unit');
    let matrix: HTMLDivElement[][] = [[], [], [], [], [], []];

    tableUnits.forEach((tableUnit: HTMLDivElement, index: number) => {
      tableUnit.classList.remove('unit-selected');
      matrix[Math.floor(index / 6)].push(tableUnit);
    });

    for (let i = 0; i < x; i++) {
      for (let j = 0; j < y; j++) {
        matrix[i][j].classList.add('unit-selected');
      }
    }

    document.querySelector(gridContext + '.table__title').textContent = this.translateService.instant('documents.editor.insertTableXY', { x: x, y: y });
  }

  insertTable(rows: number, cols: number) {
    const pos = this.editor.state.selection;
    if (this.isInsideNode(this.editor, 'table', pos.from)) return;

    this.editor.commands.insertContentAt(pos.from, '­'.repeat(rows * 6 + cols * 24));
    this.editor.commands.insertTable({ rows: rows === 1 ? 2 : rows, cols, withHeaderRow: false });
    this.editor.commands.deleteRange({ from: pos.from, to: pos.from + (rows * 6 + cols * 24 + 1) });

    if (rows === 1) {
      this.editor.commands.deleteRow();
    }
  }

  insertColumnBefore() {
    this.editor.chain().focus().addColumnBefore().run();
  }

  insertColumnAfter() {
    this.editor.chain().focus().addColumnAfter().run();
  }

  deleteColumn() {
    this.editor.chain().focus().deleteColumn().run();
  }

  insertRowBefore() {
    this.editor.chain().focus().addRowBefore().run();
  }

  insertRowAfter() {
    this.editor.chain().focus().addRowAfter().run();
  }

  deleteRow() {
    this.editor.chain().focus().deleteRow().run();
  }

  toggleHeaderCell() {
    this.editor.chain().focus().toggleHeaderCell().run();
  }

  mergeCells() {
    try {
      this.editor.chain().focus().mergeCells().run();
    } catch (e) {
      this.notyService.error(this.translateService.instant('notyService.unableToMergeCells'));
    }
  }

  splitCell() {
    this.editor.chain().focus().splitCell().run();
  }

  deleteTable() {
    this.editor.chain().focus().deleteTable().run();
  }

  positionTableMenu(editor: Editor) {
    setTimeout(() => {
      const selection = editor.state.selection;

      if (!this.isActive('table')) {
        this.tableMenuCoordinates = null;
      } else {
        const coords = editor.view.coordsAtPos(selection.from);

        this.tableMenuCoordinates = {
          top: `${coords.top + 8}px`,
          left: `${coords.left}px`
        };
      }
    });
  }

  // Imagens

  handleImageInsertion(currentEditor: Editor, files: File[], pos: number, htmlContent: string, isDropFile?: boolean) {
    files.forEach(file => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      const src = fileReader.result as string;

      if (isDropFile) {
        fileReader.onload = () => {
          currentEditor
            .chain()
            .insertContentAt(pos, {
              type: CustomImage.name,
              attrs: {
                src: fileReader.result
              }
            })
            .focus()
            .run();
        };
      } else if (!htmlContent) {
        this.insertImage(src, pos || currentEditor.state.selection.anchor);
      }
    });
  }

  insertImageWithLink() {
    const src = prompt(this.translateService.instant('documents.editor.provideImageLink'));

    this.isImageUrl(src).then(isImg => {
      if (isImg) {
        this.insertImage(src);
      } else {
        this.notyService.error(this.translateService.instant('notyService.unableToInsertImage'));
      }
    });
  }

  insertImageByFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const src = e.target.result as string;
        this.insertImage(src);
      };
      reader.readAsDataURL(file);
    }

    input.value = null;
  }

  insertImage(src: string, pos?: number) {
    const image = new Image();
    image.src = src;

    image.onload = () => {
      this.editor
        .chain()
        .insertContentAt(pos || this.editor.state.selection.anchor, {
          type: CustomImage.name,
          attrs: {
            src
          }
        })
        .focus()
        .run();
    };
  }

  async isImageUrl(url: string): Promise<boolean> {
    return fetch(url, { method: 'HEAD' })
      .then(response => {
        if (!response.ok) return false;
        const contentType = response.headers.get('content-type');
        return !!contentType && contentType.startsWith('image/');
      })
      .catch(() => {
        return false;
      });
  }

  // Temas

  changeTemplateTheme = throttle(
    (theme: DynamicTemplateThemeEnum) => {
      this.dynamicTemplateInput.theme = theme;
      this.applyDefaultAligments(true);
      this.editor.commands.focus();
    },
    100,
    { leading: true, trailing: false }
  );

  // FUNÇÕES PARA GERENCIAR CAMPOS

  onDragFieldStart(event: DragEvent, item: DynamicTemplateVariable) {
    event.dataTransfer?.setData('application/inline-field', JSON.stringify(item));
    event.dataTransfer?.setDragImage(event.target as Element, 10, 10);
  }

  onDropField(event: DragEvent) {
    event.preventDefault();

    const data = event.dataTransfer?.getData('application/inline-field');
    if (!data) return;

    const item = JSON.parse(data);

    const coords = {
      left: event.clientX,
      top: event.clientY
    };
    const pos = this.editor.getText().length === 0 ? 0 : this.editor.view.posAtCoords(coords)?.pos;

    this.insertInlineField(item, pos);
  }

  insertInlineField(item: DynamicTemplateVariable & { color?: string }, insertionIndex: number = null, formats?: Record<string, boolean>, isConvertion = false) {
    if (!item?.color) item = { ...item, color: '0' };
    if (insertionIndex === null) insertionIndex = this.editor.state.selection.from;

    if (!formats) {
      this.editor.commands.setTextSelection(insertionIndex);
      formats = this.getCurrentSelectionFormats();
    }

    this.editor
      .chain()
      .focus(insertionIndex)
      .insertContent({
        type: InlineField.name,
        attrs: item
      })
      .setTextSelection({ from: insertionIndex, to: insertionIndex + 1 })
      .setMeta('isConvertion', isConvertion)
      .run();

    if (formats.bold) {
      this.editor.chain().setBold().setMeta('addToHistory', false).run();
    }
    if (formats.italic) {
      this.editor.chain().setItalic().setMeta('addToHistory', false).run();
    }
    if (formats.underline) {
      this.editor.chain().setUnderline().setMeta('addToHistory', false).run();
    }
    if (formats.strike) {
      this.editor.chain().setStrike().setMeta('addToHistory', false).run();
    }

    this.editor.commands.setTextSelection(insertionIndex + 1);
  }

  getExistingFieldNames() {
    let names = this.customFields?.length > 0 ? this.customFields.map((field: DynamicTemplateVariable) => field.name) : [];
    if (this.fieldToEditData) {
      let str = this.fieldToEditData.name;
      let index = names.indexOf(str);
      names.splice(index, 1);
    }
    return names;
  }

  positionNewFieldBubble(editor: Editor) {
    setTimeout(() => {
      const selection = editor.state.selection;
      const selectionLength = selection.to - selection.from;

      const selectionText = editor.state.doc.textBetween(selection.from, selection.to);

      if (
        selection.empty ||
        selectionLength > 40 ||
        this.hasNodeInSelection(editor, selection, InlineField.name) ||
        this.hasNodeInSelection(editor, selection, CustomImage.name) ||
        this.hasNodeInSelection(editor, selection, Summary.name) ||
        this.validationService.validate(selectionText, ['appSpecialPattern'])
      ) {
        this.showNewFieldBubble = false;
      } else {
        this.showNewFieldBubble = true;

        const { from, to } = selection;
        const coordsStart = editor.view.coordsAtPos(from);
        const coordsEnd = editor.view.coordsAtPos(to);

        this.newFieldBubbleCoordinates = {
          top: `${coordsStart.top - 8}px`,
          left: `${(coordsStart.left + coordsEnd.right) / 2}px`
        };
      }
    });
  }

  startCreateNewField() {
    const selection: Range = this.editor.state.selection;
    const selectionLength: number = selection.to - selection.from;

    const selectionText = this.editor.state.doc.textBetween(selection.from, selection.to);

    if (selectionLength > 0 && selectionLength < 40) {
      if (
        this.hasNodeInSelection(this.editor, selection, InlineField.name) ||
        this.hasNodeInSelection(this.editor, selection, CustomImage.name) ||
        this.hasNodeInSelection(this.editor, selection, Summary.name) ||
        this.validationService.validate(selectionText, ['appSpecialPattern'])
      )
        return;

      let newFieldName = this.editor.state.doc.textBetween(selection.from, selection.to).trim();
      if (['[', '('].includes(newFieldName[0]) && [']', ')'].includes(newFieldName[newFieldName.length - 1])) {
        newFieldName = newFieldName.substring(1, newFieldName.length - 1).trim();
      }

      const formats = this.getCurrentSelectionFormats();

      this.editor.commands.deleteSelection();

      if (this.verifyFieldAlreadyExists(newFieldName)) {
        const field = this.getCustomFieldByName(newFieldName);
        this.insertInlineField(field, null, formats);

        return;
      }

      const field = this.getBaseFieldOptions(newFieldName);

      this.createNewField(field);
      this.insertInlineField(field, null, formats);
    } else {
      this.fieldToEditData = null;
      this.showCreateEditSidebar = true;
      setTimeout(() => {
        this.createEditFieldsComponent.setFieldOptionsFormValues();
        this.createEditFieldsComponent.setSignersAsSelectOptions(this.signers);
        document.querySelector('app-create-edit-fields-form').scrollTop = 0;
        this.createEditFieldsComponent.form.markAsUntouched();
      });
    }
  }

  getBaseFieldOptions(name: string) {
    return {
      name,
      type: DynamicDocumentFieldsTypes.shortTextField,
      signer: '0',
      show_help: false,
      required: true,
      help_text: '',
      regex: ''
    };
  }

  getCurrentSelectionFormats(): Record<string, boolean> {
    return {
      bold: this.isActive('bold'),
      italic: this.isActive('italic'),
      underline: this.isActive('underline'),
      strike: this.isActive('strike')
    };
  }

  handleClickOnInlineField(e: Event) {
    const target = e.target as HTMLElement;
    const targetFieldName = target.getAttribute('data-field-name');

    if (this.getCustomFieldByName(targetFieldName)) {
      let fieldData = this.getCustomFieldByName(targetFieldName);
      this.fieldToEditData = {
        name: fieldData.name,
        type: fieldData.type,
        required: fieldData.required,
        showHelp: fieldData.show_help,
        signer: target.getAttribute('data-signer-id'),
        helpText: fieldData.help_text,
        regex: fieldData.regex,
        id: target.getAttribute('data-field-id') || ''
      };

      this.showCreateEditSidebar = true;
      setTimeout(() => {
        this.createEditFieldsComponent.setFieldOptionsFormValues(this.fieldToEditData, target);
        this.createEditFieldsComponent.setSignersAsSelectOptions(this.signers);
        document.querySelector('app-create-edit-fields-form').scrollTop = 0;
      });
    } else {
      target.remove();
    }
  }

  handleFieldOptionsFormReturn(result: DynamicTemplateVariable) {
    if (!this.fieldToEditData) {
      this.createNewField(result);
    } else {
      let editedField = this.getCustomFieldByName(this.fieldToEditData.name);

      if (result && result.name) {
        this.updateCustomField(result, editedField);
      } else if (result === null) {
        this.deleteCustomField(editedField);
      }
    }
  }

  createNewField(result: DynamicTemplateVariable) {
    if (result && result.name) {
      this.customFields.push({
        name: result.name,
        type: result.type,
        show_help: result.show_help,
        required: result.required,
        help_text: result.help_text,
        regex: result.regex
      });
    }
  }

  updateCustomField(result: DynamicTemplateVariable, editedField: DynamicTemplateVariable) {
    editedField.name = result.name;
    editedField.type = result.type;
    editedField.show_help = result.show_help;
    editedField.required = result.required;
    editedField.help_text = result.help_text;
    editedField.regex = result.regex;
  }

  deleteCustomField(editedField: DynamicTemplateVariable) {
    this.customFields.splice(this.customFields.indexOf(editedField), 1);
    this.removeInlineFieldOccurrences(editedField.name);
  }

  removeInlineFieldOccurrences(field: string) {
    const { state, view } = this.editor;
    let tr = state.tr;

    const positionsToDelete: Range[] = [];

    state.doc.descendants((node, pos) => {
      if (node.isInline && node.type.name === InlineField.name && node.attrs['name'] === field) {
        positionsToDelete.push({ from: pos, to: pos + node.nodeSize });
      }
      return true;
    });

    positionsToDelete.reverse().forEach(({ from, to }) => {
      tr = tr.delete(from, to);
    });

    if (tr.docChanged) {
      tr.setMeta('addToHistory', false);
      view.dispatch(tr);
    }
  }

  removeNonExistentFields() {
    const fields = Array.from(document.querySelectorAll('.field--inline'));
    const uniqueFieldsNames = Array.from(new Set(fields.map(field => field.getAttribute('data-field-name'))));

    uniqueFieldsNames.forEach(name => {
      if (!this.verifyFieldAlreadyExists(name)) {
        this.removeInlineFieldOccurrences(name);
      }
    });
  }

  convertTypedOrPastedFields(isPasted?: boolean, range?: Range) {
    if (this.editor.getText().length === 0) return;

    const bracketContent = `[0-9a-zA-ZÀ-ÿ/\\- ]{3,40}`;
    const curlyContent = `[^{}]{0,39}\\S`;

    const wrap = (open: string, close: string, content: string) => `${open}\\s?(${content})\\s?${close}`;

    const squareBrackets = wrap(`\\[`, `\\]`, bracketContent);
    const parentheses = wrap(`\\(`, `\\)`, bracketContent);
    const doubleCurlies = wrap(`\\{\\{`, `\\}\\}`, curlyContent);

    const bracketOrParen = `(?:${squareBrackets}|${parentheses})`;
    const fullPattern = `(?:${bracketOrParen})|${doubleCurlies}`;

    const regex: RegExp = isPasted ? new RegExp(fullPattern, 'g') : new RegExp(doubleCurlies, 'g');

    const text = this.editor.getText();

    if (!text) return;

    let match: RegExpExecArray | null;
    let matches: { pos: { from: number; to: number }; isNew: boolean; item: DynamicTemplateVariable }[] = new Array();

    while ((match = regex.exec(text))) {
      const fullMatch = match[0];
      let name = (match[1] || match[2] || match[3]).trim();

      if (this.validationService.validate(name, ['appSpecialPattern'])) {
        if (!isPasted) return;
        name = name.replace(/[^\p{L}\p{N}\s]/gu, ' ');
      }

      const isOnlyNumbers = this.validationService.validate(name, ['appNotOnlyNumbers']);
      const inlineFieldOccurences = this.countNodeOccurrences(this.editor.view, 0, match.index + fullMatch.length + 1, InlineField.name);

      const templateLanguage = this.appService.getLanguageOptionFromString(this.dynamicTemplateInput.lang);

      if (
        !isOnlyNumbers &&
        (!isPasted ||
          (!this.wordsToNumberPipe.transform(name, templateLanguage) && match.index + inlineFieldOccurences >= range?.from && match.index + fullMatch.length + inlineFieldOccurences <= range?.to))
      ) {
        let posCorrectionValue = this.isInsideNode(this.editor, 'listItem', match.index + 1) ? -1 : 1;
        posCorrectionValue -= this.isInsideNode(this.editor, 'table', match.index + 1) ? 3 : 0;
        posCorrectionValue += inlineFieldOccurences;
        posCorrectionValue -= this.countNodeOccurrences(this.editor.view, 0, match.index + fullMatch.length + 1, CustomImage.name);
        posCorrectionValue -= this.countNodeOccurrences(this.editor.view, 0, match.index + fullMatch.length + 1, Summary.name);
        posCorrectionValue -= this.isInsideNode(this.editor, 'blockquote', match.index + 1) ? 1 : 0;

        const pos = { from: match.index + posCorrectionValue, to: match.index + fullMatch.length + posCorrectionValue };
        const isNew = !this.verifyFieldAlreadyExists(name);

        let item;
        if (isNew) {
          if (Array.from(new Set(matches.map(m => m.item.name.toLowerCase()))).includes(name.toLowerCase())) {
            item = matches.find(m => m.item.name.toLowerCase() === name.toLowerCase()).item;
          }
          item = this.getBaseFieldOptions(name);
        } else {
          item = this.customFields.filter(field => field.name.toLowerCase() === name.toLowerCase())[0];
        }

        matches.push({ pos, isNew, item });
      }
    }

    for (const m of matches.reverse()) {
      if (m.isNew) {
        if (!this.verifyFieldAlreadyExists(m.item.name)) {
          this.createNewField(m.item);
        } else {
          m.item = this.getCustomFieldByName(m.item.name);
        }
      }

      const formats = this.getCurrentSelectionFormats();

      this.editor.chain().deleteRange(m.pos).setMeta('isConvertion', true).run();
      this.insertInlineField(m.item, m.pos.from, formats, true);
    }
  }

  verifyFieldAlreadyExists(field: string) {
    return this.customFields.some(customField => customField.name.toLowerCase() === field?.toLowerCase());
  }

  getCustomFieldByName(name: string) {
    if (!name) return;
    return this.customFields.find(field => field.name.toLowerCase() === name.toLowerCase());
  }

  countNodeOccurrences(view: EditorView, from: number, to: number, type: string) {
    let count = 0;

    view.state.doc.nodesBetween(from, to, (node: Node) => {
      if (node.type.name === type) {
        count++;
      }
    });

    return count;
  }

  isInsideNode(editor: Editor, nodeType: string, pos: number): boolean {
    const { doc, schema } = editor.state;
    const resolvedPos = doc.resolve(pos);

    for (let i = resolvedPos.depth; i > 0; i--) {
      const node = resolvedPos.node(i);
      if (node.type === schema.nodes[nodeType]) {
        return true;
      }
    }

    return false;
  }

  hasNodeInSelection(editor: Editor, selection: Range, type: string): boolean {
    const { state } = editor;
    let found = false;

    state.doc.nodesBetween(selection.from, selection.to, node => {
      if (found) return false;

      if (node.type.name === type) {
        found = true;
        return false;
      }

      return;
    });

    return found;
  }

  // FUNÇÕES PARA SALVAR INFORMAÇÕES DO MODELO

  setTemplateLanguage(language: string) {
    this.dynamicTemplateInput.lang = this.appService.getLanguageEnumFromString(language);
  }

  saveTemplateInputData() {
    const dynamicTemplateOptions = this.form.getRawValue().dynamicTemplateOptions;

    this.dynamicTemplateInput.name = dynamicTemplateOptions.name;
    this.dynamicTemplateInput.description = dynamicTemplateOptions.description;
    this.dynamicTemplateInput.keywords = dynamicTemplateOptions.keywords.split(',').map((keyword: string) => keyword.trim());
    this.dynamicTemplateInput.category = dynamicTemplateOptions.category;
    this.dynamicTemplateInput.is_private = false;
    this.dynamicTemplateInput.lang = this.appService.getLanguageEnumFromString(dynamicTemplateOptions.lang);
    this.dynamicTemplateInput.theme = dynamicTemplateOptions.theme;

    this.removeNonExistentFields();
    this.dynamicTemplateInput.content = this.editor.getHTML();
  }

  verifyTemplateIsUnsaved() {
    if (!this.savedDynamicTemplateData) return;

    this.saveTemplateInputData();
    const currentData: SavedDynamicTemplateData = { template: cloneDeep(this.dynamicTemplateInput), variables: cloneDeep(this.customFields), signers: cloneDeep(this.signers) };
    const savedData: SavedDynamicTemplateData = cloneDeep(this.savedDynamicTemplateData);

    if (!currentData?.template.id && this.editor.isEmpty) {
      currentData.template.content = savedData?.template.content;
    }

    function fixSignersForComparison(signers: CustomSignerInput[]) {
      signers.map(signer => {
        signer.email = signer.email || null;

        type KeysOfType<T, ValueType> = {
          [K in keyof T]: T[K] extends ValueType ? K : never;
        }[keyof T];

        const attrs: KeysOfType<CustomSignerInput['_extra'], boolean>[] = ['showOptions', 'showSecurityVerifications', 'showCpfVerification', 'showSmsVerification'];
        for (const attr of attrs) {
          if (signer._extra && !signer._extra[attr]) {
            signer._extra[attr] = false;
          }
        }

        delete signer.variable_id;
        return signer;
      });
    }
    fixSignersForComparison(currentData?.signers);
    fixSignersForComparison(savedData?.signers);

    return !isEqual(currentData, savedData);
  }

  saveFieldsOnDynamicTemplateData() {
    this.variableInputs = [];

    document.querySelectorAll('.field--inline').forEach((field: HTMLElement) => {
      let fieldName = field.getAttribute('data-field-name');
      const currentField = this.customFields.filter((customField: DynamicTemplateVariable) => customField.name == fieldName)[0];
      let completeFieldData: DynamicTemplateVariableInput = { ...currentField } as DynamicTemplateVariableInput;
      completeFieldData.data_signer_id = parseInt(field.getAttribute('data-signer-id'));

      this.variableInputs.push(completeFieldData);
    });
  }

  openUnsavedAlert() {
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.goToBackWithoutSaving'),
        confirmButtonText: this.translateService.instant('button.save'),
        closeButtonText: this.translateService.instant('button.backWithoutSaving'),
        dismissAsError: true
      })
      .subscribe({
        next: () => (this.currentRightSidebarOption = 'options'),
        error: type => {
          if (type === AlertButtonType.close) {
            this.navigateToNewDocument();
          }
        }
      });
  }

  isTemplateAboveSizeLimit(): boolean {
    this.setFile();
    return this.file?.size / 1024 / 1024 > 50;
  }

  setFile() {
    const dom = this.dynamicDocumentService.getDomForDynamicDocumentFile(this.editor.getHTML(), this.dynamicTemplateInput.theme);
    const file = new File([new Blob([dom], { type: 'text/html' })], `${Math.random() * 10 ** 10}.html`, { type: 'text/html' });
    this.file = file;
  }

  navigateToNewDocument() {
    this.router.navigate(['documentos/novo'], { state: { currentTab: DocumentMainTab.TemplateUpload } });
  }

  navigateToHome() {
    this.router.navigate(['/']);
  }

  goBack(toHome = false) {
    if (this.verifyTemplateIsUnsaved()) {
      this.openUnsavedAlert();
    } else {
      toHome ? this.navigateToHome() : this.navigateToNewDocument();
    }
  }

  fixTemplateOptions() {
    const options = this.form.getRawValue().dynamicTemplateOptions;

    this.form.get('dynamicTemplateOptions.name').setValue(options.name.trim());
    this.form.get('dynamicTemplateOptions.description').setValue(options.description.trim());
    this.form.get('dynamicTemplateOptions.keywords').setValue(
      options.keywords
        .split(',')
        .map((keyword: string) => keyword.trim())
        .filter((k: string) => !!k)
        .join(', ')
    );
  }

  createTemplate() {
    this.verifyTemplateIsUnsaved();

    if (!this.dynamicTemplateInput.content || this.editor.isEmpty) {
      this.notyService.error(this.translateService.instant('notyService.emptyModelContent'));
      return;
    }

    if (this.isTemplateAboveSizeLimit()) {
      this.notyService.error(this.translateService.instant('notyService.templateSizeExceedsLimit'));
      return;
    }

    if (!this.verifyValidSigners()) {
      this.currentRightSidebarOption = 'signers';
      this.notyService.error(this.translateService.instant('notyService.noValidSigners'));
      return;
    }

    if (this.verifyDuplicateSigners()) {
      this.currentRightSidebarOption = 'signers';
      this.notyService.error(this.translateService.instant('notyService.duplicateSignersWithSameAction'));
      return;
    }

    this.showCreateEditSidebar = false;
    this.currentRightSidebarOption = 'options';

    this.fixTemplateOptions();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.loaderService.show();

    this.saveFieldsOnDynamicTemplateData();
    this.customFields.forEach(customField => {
      customField.data_signer_id = null;
      this.variableInputs.push(customField as DynamicTemplateVariableInput);
    });

    const dynamicTemplateVariableInputs = this.variableInputs.map((variableInput: DynamicTemplateVariableInput) => {
      return omit(variableInput, '__typename');
    });

    this.saveTemplateInputData();

    let dynamicTemplateSigners: SignerInput[] = [];
    this.signers.forEach((signer: CustomSignerInput, index: number) => {
      if (index === 0 && signer.action === null) return;

      let signerInput: SignerInput = this.documentFormService.transformToSignerInputsTemplate(this.document, cloneDeep(signer));

      signerInput = omit(signerInput, '__typename');
      signerInput.configs = omit(signerInput.configs, '__typename');
      signerInput.security_verifications = omit(signerInput.security_verifications, '__typename');
      signerInput.variable_id = index;

      dynamicTemplateSigners.push(signerInput);
    });

    if (!this.dynamicTemplateInput.id) {
      delete this.dynamicTemplateInput.id;

      this.dynamicDocumentService
        .create({ template: this.dynamicTemplateInput, variables: dynamicTemplateVariableInputs, signers: dynamicTemplateSigners })
        .pipe(
          tap(() => (this.isUnsaved = false)),
          finalize(() => {
            this.isLoading = false;
            this.loaderService.hide();
          })
        )
        .subscribe({
          next: () => this.navigateToNewDocument(),
          error: (error: GraphQLResponse) => {
            this.isLoading = false;
            this.loaderService.hide();
            this.errorHandlerService.handle(error);
          }
        });
    } else {
      this.dynamicDocumentService
        .update({ template: this.dynamicTemplateInput, variables: dynamicTemplateVariableInputs, signers: dynamicTemplateSigners })
        .pipe(
          tap(() => (this.isUnsaved = false)),
          finalize(() => {
            this.isLoading = false;
            this.loaderService.hide();
          })
        )
        .subscribe({
          next: () => this.navigateToNewDocument(),
          error: (error: GraphQLResponse) => {
            this.isLoading = false;
            this.loaderService.hide();
            this.errorHandlerService.handle(error);
          }
        });
    }
  }
}
